/**
 * Help — `docs/help/` as a surface inside the application.
 *
 * The guides are written for the people using the system rather than the people
 * building it, which is why they are reachable from the shell bar rather than
 * filed under Schema. Nothing here is authorized: help describes the model, and
 * a person's own access is what decides what they can actually reach.
 */

import { Link } from '@tanstack/react-router';
import { EmptyState } from '../../components/ui/feedback.tsx';
import { Card, PageHeader, Stack } from '../../components/ui/layout.tsx';
import { HELP_PAGES, helpPage } from '../../help/content.ts';
import { Prose } from './prose.tsx';

export function HelpIndexPage() {
  return (
    <Stack>
      <PageHeader
        title="Help"
        description="How the system works, in the words of somebody using it rather than building it. Every page is short on purpose."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        {HELP_PAGES.map((page) => (
          <Link
            key={page.slug}
            to="/help/$page"
            params={{ page: page.slug }}
            className="no-underline"
          >
            <Card className="h-full transition-colors hover:bg-primary-hover">
              <h2 className="text-sm font-semibold text-primary">{page.title}</h2>
              <p className="mt-1 text-sm text-tertiary">{page.summary}</p>
            </Card>
          </Link>
        ))}
      </div>
    </Stack>
  );
}

export function HelpPage({ slug }: { slug: string }) {
  const page = helpPage(slug);

  if (!page) {
    return (
      <Stack>
        <PageHeader title="No such help page" breadcrumb={<Link to="/help">Help</Link>} />
        <EmptyState
          title="Nothing is written here"
          description="The address names a page that does not exist. Everything that is written is listed on the Help index."
        />
      </Stack>
    );
  }

  return (
    <Stack>
      <Card>
        <Prose markdown={page.markdown} />
      </Card>
    </Stack>
  );
}
